import {ChartModel} from '@xh/hoist/cmp/chart';
import {HoistModel, managed, PlainObject} from '@xh/hoist/core';
import {PanelModel} from '@xh/hoist/desktop/cmp/panel';
import {action, observableRef} from '@xh/hoist/mobx';
import {SpecAssistantModel} from '../spec/assistant/SpecAssistantModel';
import {buildChart} from './ChartSpecBuilder';
import {ChartSpecModel, ChartSpecModelConfig} from './ChartSpecModel';
import {CHART_SPEC_VERSION} from './ChartSpecTypes';
import chartSpecOpsSchema from './chartSpecOps.schema.json';
import {ChartSpecConfiguratorModel} from './configurator/ChartSpecConfiguratorModel';

export interface ConfigurableChartConfig extends ChartSpecModelConfig {
    /** Title of the chart - shown in the configurator and given to the AI assistant as context. */
    title?: string;
}

/**
 * A chart configured by a ChartSpec over rows of data, with the configurator panel and AI assistant
 * that edit it. Hosts create one of these, feed it rows with `setRows()`, and render it with
 * `configurableChart()`.
 */
export class ConfigurableChartModel extends HoistModel {
    @managed readonly specModel: ChartSpecModel;
    @managed readonly chartModel = new ChartModel();
    @managed readonly configuratorModel: ChartSpecConfiguratorModel;
    @managed readonly assistantModel: SpecAssistantModel;
    @managed readonly panelModel = new PanelModel({
        modalSupport: {canOutsideClickClose: false},
        showModalToggleButton: false,
        collapsible: false,
        resizable: false
    });

    readonly title: string;

    /** The host's data - raw rows, mapped to the chart by the spec. */
    @observableRef accessor rows: PlainObject[] = [];

    constructor(config: ConfigurableChartConfig) {
        super();

        const {title, ...specConfig} = config;
        this.title = title;

        const specModel = (this.specModel = new ChartSpecModel(specConfig));
        this.assistantModel = new SpecAssistantModel({
            kind: 'chart',
            specModel,
            schemaVersion: CHART_SPEC_VERSION,
            outputSchema: chartSpecOpsSchema,
            rows: () => this.rows,
            title,
            hint: 'Describe the change, e.g. "show EPS as columns on a second axis, legend on the right".'
        });
        this.configuratorModel = new ChartSpecConfiguratorModel(specModel, this.assistantModel);

        this.addReaction({
            track: () => [specModel.spec, this.rows],
            run: () => this.render(),
            fireImmediately: true
        });
    }

    get isConfiguring(): boolean {
        return this.panelModel.isModal;
    }

    @action
    setRows(rows: PlainObject[]) {
        this.rows = rows ?? [];
    }

    clear() {
        this.setRows([]);
    }

    openConfigurator() {
        this.panelModel.setIsModal(true);
    }

    closeConfigurator() {
        this.panelModel.setIsModal(false);
    }

    //------------------------
    // Implementation
    //------------------------
    private render() {
        const {specModel, chartModel} = this,
            {config, series} = buildChart(specModel.spec, this.rows, specModel.catalog, {
                onLegendClick: field => this.toggleSeries(field)
            });

        chartModel.setHighchartsConfig(config);
        chartModel.setSeries(series);
    }

    /** A legend click shows or hides a series - as a spec change, so it is saved and undoable. */
    private toggleSeries(field: string) {
        const series = this.specModel.getSeries(field);
        if (!series) return;
        this.specModel.dispatch([{op: 'updateSeries', field, changes: {hidden: !series.hidden}}], {
            source: 'chart',
            label: `${series.hidden ? 'show' : 'hide'} ${this.specModel.seriesLabel(field)}`
        });
    }
}
